import axios, { AxiosError } from 'axios';
import customConfig from 'src/config';
import { v4 } from 'uuid';
import { getCookie, isDefaultUI, isInUIC, logoutCleanUp } from './common';
import i18n from 'i18next';
import { AccountApi } from '@Apis';
export interface ErrorInfo {
  status: number;
  data: any;
  url: string;
  traceId: string;
  message: string;
}

const request = axios.create();
/**
 * @param {Number} status error code
 */
export const errorHandle = (status: number, errorInfo: ErrorInfo) => {
  if (status === 401) {
    if (!isInUIC()) {
      if (customConfig.ui.noAuthLogOff) {
        logoutCleanUp();
        redirectToLogin();
      }
    }
    // localStorage.removeItem('casCurSelectedOrgId');
    return;
  } else if (status === 403) {
    window.showNotification({
      titleMsg: i18n.t('common.error.403'),
      severity: 'error',
    });
  }
  console.error(JSON.stringify(errorInfo, null, 2));
};

function redirectToLogin() {
  if (getCookie('login_type') === 'SSO') {
    window.location.href = customConfig.ui.clpLogoutUrl;
    return;
  }
  const tenant_id =
    new URLSearchParams(window.location.search).get('tenant_id') || 'TM';
  let loginUrl = window.location.protocol + '//' + window.location.host;
  if (isDefaultUI()) {
    loginUrl += '/login';
  }
  window.location.href =
    loginUrl + '?tenant_id=' + encodeURIComponent(tenant_id);
}

let apiPreFix = customConfig.bff.apiPreFix;
if (isInUIC() && customConfig.ui.deployEnv !== 'local') {
  apiPreFix = '/public/scf/bff';
}
const baseUrl = customConfig.ui.request.baseUrl;
request.defaults.baseURL = baseUrl + apiPreFix;

request.defaults.timeout = customConfig.ui.request.timeout;
request.defaults.headers.post['Content-Type'] = 'application/json';
request.defaults.headers.patch['Content-Type'] = 'application/json';
request.defaults.headers.put['Content-Type'] = 'application/json';
//disable get cache for IE,not support IE any more
// request.defaults.headers.get['Cache-Control'] = 'no-cache';
// request.defaults.headers.get['Pragma'] = 'no-cache';
/**
 * request interceptor
 * Before each request, if token exists, carry it in the request header
 */
let orgId = '';
export function updateOrgId(id: string) {
  orgId = id;
}

// handle auto logout
let logoutTimer: NodeJS.Timeout | null = null;
const TIMEOUT_DURATION = 60 * 60 * 1000;

async function handleTimeoutLogout() {
  if (logoutTimer) {
    clearTimeout(logoutTimer);
  }
  try {
    const res: { message: string; logoutUrl?: string } = (
      await AccountApi.logout()
    ).data;
    if (res.logoutUrl) {
      window.location.href = res.logoutUrl;
    } else {
      redirectToLogin();
    }
  } catch (e) {
    console.error(e);
  } finally {
    logoutCleanUp();
  }
}

function resetTimeoutLogout() {
  if (isInUIC()) return;
  // Skip when not authenticated (e.g. /login page) so an idle login tab
  // doesn't fire /bff/users/logout 60min later and kill another tab's session.
  if (!getCookie('login_type')) return;

  if (logoutTimer) {
    clearTimeout(logoutTimer);
  }

  logoutTimer = setTimeout(handleTimeoutLogout, TIMEOUT_DURATION);
}

resetTimeoutLogout();

function getQueryParams(params: any) {
  const queryParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'object' && value !== null) {
      queryParams.append(key, JSON.stringify(value));
    } else {
      queryParams.append(key, String(value));
    }
  }
  return queryParams.toString();
}

(window as any).handleTimeoutLogout = handleTimeoutLogout;

request.interceptors.request.use(
  (config: any) => {
    resetTimeoutLogout();

    const id = v4();
    if (isInUIC()) {
      //CAS customer header need add prefix 'tmv1-',then it can be transferred to bff by sp
      const uicToken = localStorage.getItem('uic-token') || '';
      if (uicToken) {
        config.headers['uic-token'] = uicToken;
        config.headers['x-source-domain'] = window.top?.location.origin;
      }
      config.headers['x-trace-id'] = id;
    }
    config.headers['x-request-id'] = id;
    if (!config.headers['x-org-id']) {
      config.headers['x-org-id'] = orgId ? orgId : 'all';
    }

    if (config.method === 'get' && config.params) {
      config.url += `?${getQueryParams(config.params)}`;
      delete config.params;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

request.interceptors.response.use(
  (res) => {
    if (res.request.responseType === 'blob') {
      return Promise.resolve(res);
    }
    if (200 <= res.status && res.status < 300) {
      return Promise.resolve(res.data);
    } else {
      return Promise.reject(res.data);
    }
  },
  (error) => {
    const { response } = error as AxiosError;
    if (response) {
      const errorInfo: ErrorInfo = {
        status: response.status,
        data: response.data,
        url: response.config?.url || 'unknown url',
        traceId:
          (response.config?.headers?.['x-request-id'] as string) ||
          'unknown traceId',
        message: error.message,
      };
      errorHandle(response.status, errorInfo);
      return Promise.reject(response);
    } else {
      return Promise.reject(error);
    }
  }
);

export default request;
